import { Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { CacheService } from '../cache.service';
import { CourseService } from '../course.service';
import { UserService } from '../user.service';

@Component({
    selector: 'app-course',
    templateUrl: './course.component.html',
    styleUrls: ['./course.component.scss'],
})
export class CourseComponent implements OnInit, OnDestroy {
    form;
    isAdvancedEdit = false;
    imageFileTypes = ['jpg','jpeg','png'];
    courseEditAlert;
    courseResetAlert;
    courseUpdateAlert;

    a2eOptions = {
        format: 'MM/DD/YYYY hh:mm:ss A',
        showClear: true,
        showClose: true,
        showTodayButton: true,
        useCurrent: false,
        allowInputToggle: true,
        icons: {
            time: 'fa fa-clock-o',
            date: 'fa fa-calendar',
            up: 'fa fa-chevron-up',
            down: 'fa fa-chevron-down',
            previous: 'fa fa-chevron-left',
            next: 'fa fa-chevron-right',
            today: 'fa fa-crosshairs',
            clear: 'fa fa-trash',
            close: 'fa fa-times'
            },
        widgetPositioning: {
                horizontal: 'right',
                vertical: 'top'
            }
    };

    constructor(
        private cacheService: CacheService,
        private courseService: CourseService,
        private userService: UserService,
        private fb: FormBuilder,
        private elementRef: ElementRef,
    ) {
        this.courseUpdateAlert = courseService.courseUpdate$.subscribe(() => {
            // likely already done onInit; putting this here in case it come later than init
            // console.log(`component: course update prepare form`);
            this.prepareForm();
        });

        this.courseEditAlert = courseService.courseEdit$.subscribe(() => {
            // console.log(`component: course edit prepare form`);
            this.prepareForm();
        });

        this.courseResetAlert = courseService.courseReset$.subscribe(() => {
            // console.log(`component: course form reset`);
            this.form.reset(this.course);
        });

        this.prepareForm();
    }

    ngOnInit(): void {}

    ngOnDestroy() {
        // console.log(`clean up course component`)
        this.elementRef.nativeElement.remove();
        this.courseEditAlert.unsubscribe();
        this.courseResetAlert.unsubscribe();
        this.courseUpdateAlert.unsubscribe();
    }

    prepareForm() {
        this.form = this.fb.group({
            id: [this.course.id, []],
            name: [this.course.name, [Validators.required, Validators.maxLength(300)]],
            description: [this.course.description, [Validators.required, Validators.maxLength(8000)]],
            region: [this.course.region.id, [Validators.required]],
            budgetDiscipline: [this.course.budgetDiscipline.id, []],
            status: [this.course.status.id, [Validators.required]],
            structure: [this.course.structure.id, [Validators.required]],
            calendarScope: [this.course.calendarScope.id, [Validators.required]],
            startDate: [this.course.startDate, [Validators.required]],
            endDate: [this.course.endDate, []],
            olrReady: [this.course.olrReady, [Validators.required]],
            olcReady: [this.course.olcReady, [Validators.required]],
            useAdvancedOlr: [this.course.useAdvancedOlr, [Validators.required]],
            attendEstimate: [this.course.attendEstimate, [Validators.required]],
            reportingContactHours: [this.course.reportingContactHours, [Validators.required]],
            accmeActivityID: [this.course.accmeActivityID, []],
            reportingExclusion: [this.course.reportingExclusion,[Validators.required]],
            suppressCPDRecord: [this.course.suppressCPDRecord,[]],
            contactPerson: [this.course.contactPerson.id, []],
            contactPhone: [this.course.contactPhone, []],
            brochureUrl: [this.course.brochureUrl, [Validators.maxLength(100)]],
            internalComment: [this.course.internalComment,[Validators.maxLength(2000)]],
            purposeGoals: [this.course.purposeGoals,[Validators.maxLength(500)]],
            topicKeywords: [this.course.topicKeywords,[Validators.maxLength(300)]],
            targetAudience: [this.course.targetAudience,[Validators.maxLength(500)]],
            registration: [this.course.registration,[Validators.required]],
            calendarReminder: [this.course.calendarReminder,[]],
            image: [this.course.image,[]],
            // trackSelectionRequired: [this.course.trackSelectionRequired, [Validators.required]],
            // reportingHeadcount: [this.course.reportingHeadcount,[Validators.required]],
            // cipeEvent: [this.course.cipeEvent, [Validators.required]],
            // multiAhecEvent: [this.course.multiAhecEvent, [Validators.required]],
            // reportingCrosstabHeader: [this.course.reportingCrosstabHeader,[Validators.required]],
            // parsSponsorship: [this.course.parsSponsorship,[Validators.required]],
            tagRegions: [(this.course.tagRegions)?this.course.tagRegions.map((region)=>region.id):null, []],
            tagStatewide: [(this.course.tagStatewide)?this.course.tagStatewide.map((tag)=>tag.id):null, []],
            // showLmsOpen: [this.course.showLmsOpen, []],
            joinUrl: [this.course.joinUrl, [Validators.maxLength(400)]],
            landingPage: [this.course.landingPage, [Validators.required]],
            landingPageImage: [this.course.landingPageImage,[]],
            welcomeMessage: [this.course.welcomeMessage, [Validators.maxLength(2000)]],
        });
        
        // used by image filter
        this.region.valueChanges.subscribe((value) => {
            this.courseService.updateRegion(value)
        });

        // used by image filter
        this.budgetDiscipline.valueChanges.subscribe((value) => {
            this.courseService.updateBudgetDiscipline(value)
        });

        this.form.valueChanges.subscribe((value) => {
            this.courseService.isValid = this.form.dirty && !this.form.invalid;
            this.courseService.updateFormData(value)
        });
        
        // send inital formData to service
        this.courseService.updateFormData(this.form.value);

    }

    collapse() {
        this.courseService.display = !this.courseService.display;
    }

    reload() {
        this.courseService.get();
    }

    open(url){
        window.open(url, "_blank");
    }

    get errorMessage() {
        return this.courseService.errorMessage;
    }
    
    get isEditable() {
        return this.courseService.isEditable;
    }
    
    get isLoaded() {
        return this.courseService.isLoading;
    }

    get isLoading() {
        return this.courseService.isLoading;
    }
    
    get isSaving() {
        return this.courseService.isSaving;
    }
    
    get isDeleting() {
        return this.courseService.isDeleting;
    }
    
    get display() {
        return this.courseService.display;
    }
    
    get course() {
        return this.courseService.course;
    }
    
    get regions() {
        return this.cacheService.regions;
    }

    get tagsStatewide() {
        return this.cacheService.tagsStatewide;
    }

    get regionsScoped() {
        return this.cacheService.regionsScoped;
    }
    
    get budgetDisciplines() {
        return this.cacheService.budgetDisciplines;
    }
    
    get statuses() {
        return this.cacheService.statuses;
    }
    
    get structures() {
        return this.cacheService.structures;
    }
    
    get calendarScopes() {
        return this.cacheService.calendarScopes;
    }
    
    get active() {
        return this.cacheService.active;
    }

    get yesNo() {
        return this.cacheService.yesNo;
    }

    get calendarReminderOptions() {
        return this.cacheService.calendarReminder;
    }

    get calendarReminderName() {
        if (this.course.calendarReminder === null) return null;
        let name = null;
        this.calendarReminderOptions.forEach(o => {
            if (o.value === this.course.calendarReminder) {
                name = o.name;
            }
        });
        return name;
    }
    
    get isLoadingCache() {
        return this.cacheService.isLoading;
    }
    
    get name() {
        return this.form.get('name');
    }
    
    get description() {
        return this.form.get('description');
    }
    
    get region() {
        return this.form.get('region');
    }

    get tagStatewide() {
        return this.form.get('tagStatewide');
    }
    
    get budgetDiscipline() {
        return this.form.get('budgetDiscipline');
    }
    
    get status() {
        return this.form.get('status');
    }
    
    get structure() {
        return this.form.get('structure');
    }
    
    get calendarScope() {
        return this.form.get('calendarScope');
    }
    
    get startDate() {
        return this.form.get('startDate');
    }
    
    get endDate() {
        return this.form.get('endDate');
    }
    
    get olrReady() {
        return this.form.get('olrReady');
    }
    
    get olcReady() {
        return this.form.get('olcReady');
    }
    
    get useAdvancedOlr() {
        return this.form.get('useAdvancedOlr');
    }
    
    get attendEstimate() {
        return this.form.get('attendEstimate');
    }
    
    get reportingContactHours() {
        return this.form.get('reportingContactHours');
    }

    get accmeActivityID() {
        return this.form.get('accmeActivityID');
    }
    
    get reportingExclusion() {
        return this.form.get('reportingExclusion');
    }

    get suppressCPDRecord() {
        return this.form.get('suppressCPDRecord');
    }

    get contactPerson() {
        return this.form.get('contactPerson');
    }
    
    get contactPhone() {
        return this.form.get('contactPhone');
    }
    
    get brochureUrl() {
        return this.form.get('brochureUrl');
    }
    
    get internalComment() {
        return this.form.get('internalComment');
    }
    
    get purposeGoals() {
        return this.form.get('purposeGoals');
    }
    
    get topicKeywords() {
        return this.form.get('topicKeywords');
    }
    
    get targetAudience() {
        return this.form.get('targetAudience');
    }

    get registration() {
        return this.form.get('registration');
    }

    get calendarReminder() {
        return this.form.get('calendarReminder');
    }
    
    get image() {
        return this.form.get('image');
    }
    
    get submitted() {
        return this.courseService.submitted;
    }
    
    // get trackSelectionRequired() {
    //     return this.form.get('trackSelectionRequired');
    // }
    
    // get reportingHeadcount() {
    //     return this.form.get('reportingHeadcount');
    // }
    
    // get cipeEvent() {
    //     return this.form.get('cipeEvent');
    // }
    
    // get multiAhecEvent() {
    //     return this.form.get('multiAhecEvent');
    // }
    
    // get reportingCrosstabHeader() {
    //     return this.form.get('reportingCrosstabHeader');
    // }
    
    // get parsSponsorship() {
    //     return this.form.get('parsSponsorship');
    // }

    get joinUrl() {
        return this.form.get('joinUrl');
    }
    get landingPage() {
        return this.form.get('landingPage');
    }
    get welcomeMessage() {
        return this.form.get('welcomeMessage');
    }
    get isSuperAdmin() {
        return this.userService.isSuperAdmin;
    }

}
